import type { ActionKind, Avatar, GameView, Role, SeatView } from "@coup/shared";
import { ROLE_ART } from "../../art";

const UNKNOWN: Avatar = { kind: "preset", color: "slate", emblem: "mask", frame: "silver" };

/** Looks up seat names and avatars by player id. */
export function makeNames(seats: SeatView[], you?: string) {
  const byId = new Map(seats.map((s) => [s.id, s]));
  return {
    seat: (id?: string) => (id ? byId.get(id) : undefined),
    name: (id?: string) => (id ? (byId.get(id)?.name ?? "Someone") : ""),
    /** "you" for the local player, otherwise the name. */
    who: (id?: string) => (id === you ? "you" : id ? (byId.get(id)?.name ?? "Someone") : ""),
    avatar: (id?: string) => (id && byId.get(id)?.avatar) || UNKNOWN,
  };
}

export type Names = ReturnType<typeof makeNames>;

export const roleName = (r?: Role) => (r ? ROLE_ART[r].name : "");

export const ACTION_LABEL: Record<ActionKind, string> = {
  income: "Income",
  foreignAid: "Foreign Aid",
  tax: "Tax",
  exchange: "Exchange",
  assassinate: "Assassinate",
  steal: "Steal",
  coup: "Coup",
};

/** Short status line shown in the middle of the table. */
export function caption(view: GameView, n: Names): string {
  const p = view.phase;
  const a = view.action;
  const you = view.you;
  switch (p.t) {
    case "awaitAction":
      return view.turn.playerId === you ? "Your turn" : `${n.name(view.turn.playerId)} to play`;
    case "window":
      if (p.stage === "block") return `${n.name(p.claimant)} blocks with ${roleName(p.claim)}`;
      if (p.stage === "blockOnly") return `${n.name(a?.target)} may block`;
      if (!a?.claim) return `${n.name(a?.actor)} takes ${ACTION_LABEL[a!.kind]}`;
      return a.target === you
        ? `${n.name(a.actor)} claims ${roleName(a.claim)} against you`
        : `${n.name(a.actor)} claims ${roleName(a.claim)}`;
    case "challenge":
      return p.accused === you
        ? `Your ${roleName(p.role)} is challenged`
        : `${n.name(p.challenger)} calls Bullshit on ${n.name(p.accused)}`;
    case "loseCard":
      return p.playerId === you
        ? "Choose a card to lose"
        : `${n.name(p.playerId)} is losing a card`;
    case "exchange":
      return p.playerId === you ? "You claim Ambassador" : `${n.name(p.playerId)} is exchanging`;
    case "over":
      return "Match over";
  }
}
